import { Link, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import ChevronRight from "~icons/material-symbols/chevron-right";
import QrCodeScanner from "~icons/material-symbols/qr-code-scanner";
import Refresh from "~icons/material-symbols/refresh";
import Send from "~icons/material-symbols/send-outline";
import VolunteerActivism from "~icons/material-symbols/volunteer-activism-outline";
import { css } from "../../../../styled-system/css";
import { Card } from "../../../components/card";
import type { TenantWithFeature } from "../../../libs/connect";
import { formatDate } from "../../../libs/date";
import { t } from "../../../libs/i18n";
import { scanQrCode } from "../../../libs/qr-scanner";
import { formatNumber } from "../../../libs/utils";
import { CardHeader } from "./card-header";

type WalletCardProps = {
  tenant: TenantWithFeature;
  totalAmount: number;
  nearestExpireAtDate: Date | null;
  showChargeAction?: boolean;
};

type ActionButtonProps = {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
};

const ActionButton = ({ icon, label, onClick }: ActionButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    className={css({
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "4px",
      cursor: "pointer",
    })}
  >
    <div
      className={css({
        w: "42px",
        h: "42px",
        bg: "background.background",
        rounded: "full",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "text.secondary",
        fontSize: "24px",
      })}
    >
      {icon}
    </div>
    <div
      className={css({
        fontSize: "12px",
        color: "text.primary",
        textAlign: "center",
        lineHeight: "1.35",
        whiteSpace: "nowrap",
      })}
    >
      {label}
    </div>
  </button>
);

export const WalletCard = ({
  tenant,
  totalAmount,
  nearestExpireAtDate,
  showChargeAction = tenant.feature.charge,
}: WalletCardProps) => {
  const navigate = useNavigate();

  const handlePay = useCallback(async () => {
    await scanQrCode({
      filterServiceIds: [tenant.serviceId],
      navigateFn: navigate,
    });
  }, [navigate, tenant.serviceId]);

  const handleSend = useCallback(() => {
    navigate({ to: "/transfer" });
  }, [navigate]);

  const handleReceive = useCallback(() => {
    navigate({ to: "/receive" });
  }, [navigate]);

  const handleCharge = useCallback(() => {
    navigate({ to: "/charge" });
  }, [navigate]);

  return (
    <Card header={<CardHeader tenant={tenant} />}>
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "24px",
        })}
      >
        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
            w: "full",
          })}
        >
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
              w: "full",
            })}
          >
            <div
              className={css({
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                w: "full",
                px: "20px",
              })}
            >
              <div
                className={css({
                  fontSize: "14px",
                  color: "text.secondary",
                  textAlign: "center",
                  lineHeight: "1.4",
                })}
              >
                {t("ポイント残高")}
              </div>
              <div
                className={css({
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  w: "full",
                })}
              >
                <div
                  className={css({
                    display: "flex",
                    alignItems: "flex-end",
                    gap: "2px",
                  })}
                >
                  <div
                    className={css({
                      fontSize: "40px",
                      fontWeight: 400,
                      color: "text.primary",
                      lineHeight: "1.38",
                      letterSpacing: "-0.4px",
                    })}
                  >
                    {formatNumber(totalAmount)}
                  </div>
                  <div
                    className={css({
                      fontSize: "16px",
                      color: "text.secondary",
                      lineHeight: "1.5",
                      pb: "8px",
                    })}
                  >
                    pt
                  </div>
                </div>
              </div>
            </div>

            {nearestExpireAtDate && (
              <div
                className={css({
                  bg: "#E1E5FF",
                  border: "1px solid",
                  borderColor: "border.secondary",
                  rounded: "12px",
                  px: "16px",
                  py: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                })}
              >
                <div
                  className={css({
                    fontSize: "14px",
                    color: "text.secondary",
                    lineHeight: "1.4",
                  })}
                >
                  {t("{date}に", {
                    date: formatDate(nearestExpireAtDate, "dateTimeSlim"),
                  })}
                  <br />
                  {t("失効するポイントがあります")}
                </div>
                <Link
                  to="/balances"
                  className={css({
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                    color: "text.accentPrimary",
                  })}
                >
                  <div className={css({ fontSize: "12px" })}>{t("内訳")}</div>
                  <ChevronRight
                    className={css({
                      fontSize: "24px",
                      color: "text.accentPrimary",
                    })}
                  />
                </Link>
              </div>
            )}
          </div>
        </div>

        {(tenant.ui.homeActionCards.showPay ||
          (tenant.feature.transfer && tenant.ui.homeActionCards.showSend) ||
          tenant.ui.homeActionCards.showReceive ||
          showChargeAction) && (
          <div
            className={css({
              display: "flex",
              alignItems: "center",
              gap: "32px",
            })}
          >
            {tenant.ui.homeActionCards.showPay && (
              <ActionButton
                icon={<QrCodeScanner fontSize={20} />}
                label={t("支払う")}
                onClick={handlePay}
              />
            )}
            {tenant.feature.transfer && tenant.ui.homeActionCards.showSend && (
              <ActionButton
                icon={
                  <Send
                    fontSize={20}
                    className={css({ transform: "translateX(1px)" })}
                  />
                }
                label={t("送る")}
                onClick={handleSend}
              />
            )}
            {tenant.ui.homeActionCards.showReceive && (
              <ActionButton
                icon={<VolunteerActivism fontSize={20} />}
                label={t("受け取る")}
                onClick={handleReceive}
              />
            )}
            {showChargeAction && (
              <ActionButton
                icon={<Refresh fontSize={20} />}
                label={t("チャージ")}
                onClick={handleCharge}
              />
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
